import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { workspacePath } from "../lib/workspaces";
import { apiGet, apiPost, apiPatch, apiDelete, downloadQr, fetchQrObjectUrl } from "../lib/api";
import { Plus, QrCode, Pencil, Trash2, X, Download, Copy, Check, Users } from "lucide-react";
import { ArrowDown, ArrowUp, ClipboardList } from "lucide-react";
import { useAuth } from "../lib/auth";
import type { CustomFieldDefinition } from "@vms/shared";

interface LocationRecord {
  id: string;
  name: string;
  address: string | null;
  isActive: boolean;
  checkinUrl: string;
  requiredScopes: string[];
  autoCheckoutHours: number;
  customFields: CustomFieldDefinition[] | null;
  _count?: { checkIns: number; hosts: number };
}

interface HostRecord {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
}

export function Locations() {
  const [locations, setLocations] = useState<LocationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<LocationRecord | null>(null);
  const [qrLocationId, setQrLocationId] = useState<string | null>(null);
  const [hostsLocation, setHostsLocation] = useState<LocationRecord | null>(null);
  const [plan, setPlan] = useState<string>("FREE");
  const { role } = useAuth();
  const canEdit = role === "ADMIN";

  async function fetchLocations() {
    const res = await apiGet<LocationRecord[]>("/locations");
    if (res.success && res.data) setLocations(res.data);
    setLoading(false);
  }

  useEffect(() => {
    fetchLocations();
    apiGet<{ plan: string }>("/billing").then((res) => {
      if (res.success && res.data) setPlan(res.data.plan);
    });
  }, []);

  function openCreate() {
    setEditing(null);
    setShowForm(true);
  }

  function openEdit(loc: LocationRecord) {
    setEditing(loc);
    setShowForm(true);
  }

  async function handleDelete(id: string) {
    if (!confirm("Are you sure you want to delete this location?")) return;
    await apiDelete(`/locations/${id}`);
    fetchLocations();
  }

  function handleDownloadQr(locationId: string, format: "svg" | "png") {
    void downloadQr(locationId, format).catch((err) => console.error("[qr]", err));
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-stone-900 font-display">Locations</h1>
        {canEdit && <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
        >
          <Plus size={16} />
          Add Location
        </button>}
      </div>

      {loading ? (
        <p className="text-stone-500">Loading...</p>
      ) : locations.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
          <MapPinIcon />
          <p className="mt-4 text-stone-500">No locations yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((loc) => (
            <div key={loc.id} className="bg-white rounded-xl border border-stone-200 p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-medium text-stone-900">{loc.name}</h3>
                  {loc.address && <p className="text-sm text-stone-500 mt-0.5">{loc.address}</p>}
                </div>
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    loc.isActive ? "bg-green-50 text-green-700" : "bg-stone-100 text-stone-500"
                  }`}
                >
                  {loc.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              {loc.autoCheckoutHours > 0 && (
                <p className="text-xs text-stone-400 mb-3">Auto-checkout: {loc.autoCheckoutHours}h</p>
              )}
              <p className="text-xs text-stone-500 mb-3">{loc.customFields?.length ?? 0} manual check-in fields</p>
              <p className="text-xs text-stone-500 mb-3">
                {loc._count?.hosts
                  ? `${loc._count.hosts} host${loc._count.hosts === 1 ? "" : "s"} — visitors pick one and it is emailed on arrival`
                  : "No hosts — visitors check in without naming who they are here to see"}
              </p>

              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-stone-100">
                {canEdit && <button
                  onClick={() => setHostsLocation(loc)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg"
                >
                  <Users size={14} />
                  Hosts
                </button>}
                {canEdit && <button
                  onClick={() => setQrLocationId(loc.id)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg"
                >
                  <QrCode size={14} />
                  QR Code
                </button>}
                {canEdit && <button
                  onClick={() => openEdit(loc)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg"
                >
                  <Pencil size={14} />
                  Edit
                </button>}
                {canEdit && <button
                  onClick={() => handleDelete(loc.id)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg ml-auto"
                >
                  <Trash2 size={14} />
                </button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Location form modal */}
      {showForm && (
        <LocationFormModal
          editing={editing}
          plan={plan}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchLocations(); }}
        />
      )}

      {/* Host directory modal */}
      {hostsLocation && (
        <HostsModal
          location={hostsLocation}
          onClose={() => { setHostsLocation(null); fetchLocations(); }}
        />
      )}

      {/* QR code modal */}
      {qrLocationId && (
        <QrModal
          locationId={qrLocationId}
          checkinUrl={locations.find((l) => l.id === qrLocationId)?.checkinUrl ?? ""}
          onClose={() => setQrLocationId(null)}
          onDownload={handleDownloadQr}
        />
      )}
    </div>
  );
}

function MapPinIcon() {
  return (
    <div className="inline-flex items-center justify-center w-12 h-12 bg-stone-100 rounded-full">
      <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth={1.5} className="text-stone-400">
        <path d="M12 21s-6-5.686-6-10A6 6 0 0 1 18 11c0 4.314-6 10-6 10Z" />
        <circle cx="12" cy="11" r="2" />
      </svg>
    </div>
  );
}

// ── Location form modal ──

const AVAILABLE_SCOPES: { key: string; label: string; required?: boolean }[] = [
  { key: "name", label: "Name", required: true },
  { key: "uinfin", label: "NRIC/FIN", required: true },
  { key: "dob", label: "Date of Birth" },
  { key: "mobileno", label: "Mobile Number" },
  { key: "email", label: "Email Address" },
];

const ALWAYS_INCLUDED = ["openid", "name", "uinfin"];
// Scopes gated behind a paid plan — the Free tier collects identity only. Must
// mirror PLAN_SCOPES on the backend, which is the enforcement authority.
const PAID_ONLY_SCOPES = ["dob", "mobileno", "email"];

function LocationFormModal({
  editing,
  plan,
  onClose,
  onSaved,
}: {
  editing: LocationRecord | null;
  plan: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isFree = plan === "FREE";
  const customFieldsEnabled = ["PROFESSIONAL", "BUSINESS", "ENTERPRISE"].includes(plan);
  const [name, setName] = useState(editing?.name || "");
  const [address, setAddress] = useState(editing?.address || "");
  const [autoCheckoutHours, setAutoCheckoutHours] = useState(editing?.autoCheckoutHours || 0);
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(() => {
    const fullDefault = ["openid", "name", "uinfin", "dob", "mobileno", "email"];
    const scopes = editing?.requiredScopes ?? (isFree ? ALWAYS_INCLUDED : fullDefault);
    // Never surface paid-only scopes as selected on Free (e.g. a location saved
    // before a downgrade) — the backend would reject the save.
    return new Set(isFree ? scopes.filter((s) => !PAID_ONLY_SCOPES.includes(s)) : scopes);
  });
  const [saving, setSaving] = useState(false);
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>(editing?.customFields ?? []);
  const [error, setError] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const initialState = JSON.stringify({ name: editing?.name || "", address: editing?.address || "", autoCheckoutHours: editing?.autoCheckoutHours || 0, customFields: editing?.customFields ?? [] });
  const dirty = JSON.stringify({ name, address, autoCheckoutHours, customFields }) !== initialState;

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (dirty) event.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  function close() {
    if (!dirty || confirm("Discard unsaved location changes?")) onClose();
  }

  function toggleScope(key: string) {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setErrorKey(null);

    const requiredScopes = [...new Set([...ALWAYS_INCLUDED, ...selectedScopes])];
    const body = { name, address: address || undefined, autoCheckoutHours, requiredScopes, ...(customFieldsEnabled && { customFields }) };
    const res = editing
      ? await apiPatch(`/locations/${editing.id}`, body)
      : await apiPost("/locations", body);

    if (res.success) {
      onSaved();
    } else {
      setError(res.error?.message || "Failed to save");
      setErrorKey(res.error?.key ?? null);
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={close}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-stone-900 font-display">
            {editing ? "Edit Location" : "New Location"}
          </h2>
          <button onClick={close} className="p-1 hover:bg-stone-100 rounded-lg">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="Main Office"
            />
          </div>

          <CustomFieldBuilder fields={customFields} setFields={setCustomFields} disabled={!customFieldsEnabled} />
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Address</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="1 Raffles Place, Singapore"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Auto-checkout (hours, 0 = disabled)
            </label>
            <input
              type="number"
              min={0}
              value={autoCheckoutHours}
              onChange={(e) => setAutoCheckoutHours(Number(e.target.value))}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">Visitor data fields</label>
            <p className="text-xs text-stone-400 mb-2">Select which Singpass/Myinfo fields to collect during check-in.</p>
            <div className="space-y-1.5">
              {AVAILABLE_SCOPES.map((scope) => {
                const locked = isFree && PAID_ONLY_SCOPES.includes(scope.key);
                const disabled = scope.required || locked;
                return (
                  <label
                    key={scope.key}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border text-sm transition-colors ${
                      selectedScopes.has(scope.key)
                        ? "border-brand-200 bg-brand-50"
                        : "border-stone-200 hover:bg-stone-50"
                    } ${disabled ? "opacity-75 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedScopes.has(scope.key) && !locked}
                      onChange={() => !disabled && toggleScope(scope.key)}
                      disabled={disabled}
                      className="rounded border-stone-300 text-brand-500 focus:ring-brand-500"
                    />
                    <span className="text-stone-700">{scope.label}</span>
                    {scope.required && <span className="text-xs text-stone-400 ml-auto">Required</span>}
                    {locked && (
                      <span className="text-xs text-brand-600 ml-auto">Paid plan</span>
                    )}
                  </label>
                );
              })}
            </div>
            {isFree && (
              <p className="text-xs text-stone-500 mt-2">
                The Free plan collects Singpass-verified name and NRIC only.{" "}
                <Link to={workspacePath("billing")} className="underline font-medium text-brand-600 hover:text-brand-700">
                  Upgrade
                </Link>{" "}
                to collect date of birth, mobile, and email.
              </p>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {error}
              {errorKey === "PLAN_LIMIT" && (
                <>
                  {" "}
                  <Link to={workspacePath("billing")} className="underline font-medium hover:text-red-700">
                    Upgrade your plan
                  </Link>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={close}
              className="px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const FIELD_TYPES: Array<{ value: CustomFieldDefinition["type"]; label: string }> = [
  ["short_text", "Short text"], ["long_text", "Long text"], ["single_select", "Single select"],
  ["multi_select", "Multiple select"], ["boolean", "Yes / no"], ["number", "Number"],
  ["date", "Date"], ["email", "Email"], ["phone", "Phone"],
].map(([value, label]) => ({ value: value as CustomFieldDefinition["type"], label }));

function CustomFieldBuilder({ fields, setFields, disabled }: { fields: CustomFieldDefinition[]; setFields: (fields: CustomFieldDefinition[]) => void; disabled: boolean }) {
  function add() {
    if (fields.length >= 20) return;
    setFields([...fields, { id: crypto.randomUUID(), label: "New field", type: "short_text", required: false }]);
  }
  function patch(index: number, value: Partial<CustomFieldDefinition>) {
    setFields(fields.map((field, i) => i === index ? { ...field, ...value } : field));
  }
  function move(index: number, offset: number) {
    const next = [...fields]; const target = index + offset;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]]; setFields(next);
  }
  return <div className="border-t border-stone-200 pt-4">
    <div className="flex justify-between"><div><p className="text-sm font-medium text-stone-700">Manual check-in fields</p><p className="text-xs text-stone-400">{fields.length}/20 fields</p></div><button type="button" onClick={add} disabled={disabled || fields.length >= 20} className="text-sm text-brand-600 disabled:text-stone-400"><Plus size={14} className="inline" /> Add field</button></div>
    {disabled && <p className="text-xs text-stone-500 mt-2">Custom fields require the Professional plan or higher. Saved fields remain available after upgrading.</p>}
    <div className="space-y-3 mt-3">{fields.map((field, index) => <div key={field.id} className="rounded-lg border border-stone-200 p-3 space-y-2">
      <div className="flex gap-2"><input aria-label="Field label" disabled={disabled} value={field.label} maxLength={100} onChange={(e) => patch(index, { label: e.target.value })} className="flex-1 px-2 py-1.5 border rounded text-sm" /><select disabled={disabled} value={field.type} onChange={(e) => { const type = e.target.value as CustomFieldDefinition["type"]; patch(index, { type, options: type.includes("select") ? field.options ?? [{ id: crypto.randomUUID(), label: "Option 1" }] : undefined }); }} className="px-2 border rounded text-sm">{FIELD_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select></div>
      <input aria-label="Help text" disabled={disabled} value={field.helpText ?? ""} maxLength={300} onChange={(e) => patch(index, { helpText: e.target.value || undefined })} placeholder="Help text (optional)" className="w-full px-2 py-1.5 border rounded text-sm" />
      {!field.type.includes("select") && field.type !== "boolean" && <input aria-label="Placeholder" disabled={disabled} value={field.placeholder ?? ""} maxLength={100} onChange={(e) => patch(index, { placeholder: e.target.value || undefined })} placeholder="Placeholder (optional)" className="w-full px-2 py-1.5 border rounded text-sm" />}
      {(field.type === "single_select" || field.type === "multi_select") && <div className="space-y-1">{field.options?.map((option, optionIndex) => <div key={option.id} className="flex gap-1"><input disabled={disabled} value={option.label} maxLength={100} onChange={(e) => patch(index, { options: field.options?.map((item, i) => i === optionIndex ? { ...item, label: e.target.value } : item) })} className="flex-1 px-2 py-1 border rounded text-sm" /><button type="button" disabled={disabled} onClick={() => patch(index, { options: field.options?.filter((_, i) => i !== optionIndex) })}>×</button></div>)}<button type="button" disabled={disabled || (field.options?.length ?? 0) >= 30} onClick={() => patch(index, { options: [...(field.options ?? []), { id: crypto.randomUUID(), label: `Option ${(field.options?.length ?? 0) + 1}` }] })} className="text-xs text-brand-600">Add option</button></div>}
      <div className="flex items-center gap-2"><label className="text-xs"><input disabled={disabled} type="checkbox" checked={field.required} onChange={(e) => patch(index, { required: e.target.checked })} /> Required</label><span className="ml-auto" /><button aria-label="Move field up" type="button" disabled={disabled || index === 0} onClick={() => move(index, -1)}><ArrowUp size={15} /></button><button aria-label="Move field down" type="button" disabled={disabled || index === fields.length - 1} onClick={() => move(index, 1)}><ArrowDown size={15} /></button><button aria-label="Duplicate field" type="button" disabled={disabled || fields.length >= 20} onClick={() => setFields([...fields.slice(0, index + 1), { ...field, id: crypto.randomUUID(), options: field.options?.map((option) => ({ ...option, id: crypto.randomUUID() })) }, ...fields.slice(index + 1)])}><Copy size={15} /></button><button aria-label="Delete field" type="button" disabled={disabled} onClick={() => setFields(fields.filter((_, i) => i !== index))}><Trash2 size={15} /></button></div>
    </div>)}</div>
    {fields.length > 0 && <div className="mt-4 rounded-xl bg-stone-50 border border-stone-200 p-4"><p className="text-xs font-medium uppercase text-stone-500 flex items-center gap-1"><ClipboardList size={13} /> Mobile preview</p>{fields.map((field) => <div key={field.id} className="mt-3"><p className="text-sm font-medium">{field.label}{field.required && " *"}</p>{field.helpText && <p className="text-xs text-stone-500">{field.helpText}</p>}<div className="h-9 mt-1 rounded-lg border bg-white" /></div>)}</div>}
  </div>;
}

// ── Host directory modal ──

function HostsModal({ location, onClose }: { location: LocationRecord; onClose: () => void }) {
  const [hosts, setHosts] = useState<HostRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");

  async function load() {
    const res = await apiGet<HostRecord[]>(`/locations/${location.id}/hosts`);
    if (res.success && res.data) setHosts(res.data);
    setLoading(false);
  }

  useEffect(() => { load(); }, [location.id]);

  async function add(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const res = await apiPost(`/locations/${location.id}/hosts`, { name, email });
    if (res.success) {
      setName("");
      setEmail("");
      await load();
    } else {
      setError(res.error?.message ?? "Failed to add host");
    }
    setSaving(false);
  }

  function startEdit(host: HostRecord) {
    setEditingId(host.id);
    setEditName(host.name);
    setEditEmail(host.email);
    setError(null);
  }

  async function saveEdit(hostId: string) {
    setSaving(true);
    setError(null);
    const res = await apiPatch(`/locations/${location.id}/hosts/${hostId}`, { name: editName, email: editEmail });
    if (res.success) {
      setEditingId(null);
      await load();
    } else {
      setError(res.error?.message ?? "Failed to save host");
    }
    setSaving(false);
  }

  async function setActive(host: HostRecord, isActive: boolean) {
    setError(null);
    const res = isActive
      ? await apiPatch(`/locations/${location.id}/hosts/${host.id}`, { isActive: true })
      : await apiDelete(`/locations/${location.id}/hosts/${host.id}`);
    if (!res.success) setError(res.error?.message ?? "Failed to update host");
    await load();
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-stone-900 font-display">Hosts — {location.name}</h2>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded-lg"><X size={18} /></button>
        </div>
        <p className="text-xs text-stone-500 mb-4">
          Visitors choose a host at check-in and that host is emailed on arrival. With no active hosts, check-in
          skips the question and no email is sent.
        </p>

        {loading ? (
          <p className="text-stone-500 text-sm">Loading...</p>
        ) : hosts.length === 0 ? (
          <p className="text-sm text-stone-500 mb-4">No hosts yet.</p>
        ) : (
          <ul className="divide-y divide-stone-100 mb-4">
            {hosts.map((host) => (
              <li key={host.id} className="py-2">
                {editingId === host.id ? (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input aria-label="Host name" value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 px-2 py-1.5 border border-stone-300 rounded text-sm" />
                    <input aria-label="Host email" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} className="flex-1 px-2 py-1.5 border border-stone-300 rounded text-sm" />
                    <div className="flex gap-2">
                      <button disabled={saving} onClick={() => saveEdit(host.id)} className="px-3 py-1.5 text-xs font-medium text-white bg-brand-500 rounded-lg disabled:opacity-50">Save</button>
                      <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-xs font-medium text-stone-600 hover:bg-stone-100 rounded-lg">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm truncate ${host.isActive ? "text-stone-900" : "text-stone-400"}`}>{host.name}</p>
                      <p className="text-xs text-stone-500 truncate">{host.email}</p>
                    </div>
                    {!host.isActive && <span className="px-2 py-0.5 text-xs rounded-full bg-stone-100 text-stone-500">Inactive</span>}
                    <button onClick={() => startEdit(host)} className="p-1.5 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded-lg" aria-label={`Edit ${host.name}`}><Pencil size={14} /></button>
                    <button onClick={() => setActive(host, !host.isActive)} className="px-2.5 py-1.5 text-xs font-medium text-stone-600 hover:bg-stone-100 rounded-lg">
                      {host.isActive ? "Deactivate" : "Reactivate"}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={add} className="flex flex-col gap-2 sm:flex-row border-t border-stone-200 pt-4">
          <input required aria-label="New host name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm" />
          <input required type="email" aria-label="New host email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="host@company.com" className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm" />
          <button type="submit" disabled={saving} className="px-4 py-2 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">Add</button>
        </form>

        {error && <div className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>}
      </div>
    </div>
  );
}

// ── QR Code modal ──

function QrModal({
  locationId,
  checkinUrl,
  onClose,
  onDownload,
}: {
  locationId: string;
  checkinUrl: string;
  onClose: () => void;
  onDownload: (id: string, format: "svg" | "png") => void;
}) {
  // An <img> cannot send an Authorization header, so the SVG is fetched and
  // shown from an object URL rather than pointing the browser at the endpoint.
  const [qrSvgUrl, setQrSvgUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchQrObjectUrl(locationId, "svg")
      .then((url) => {
        objectUrl = url;
        if (cancelled) URL.revokeObjectURL(url);
        else setQrSvgUrl(url);
      })
      .catch((err) => console.error("[qr]", err));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [locationId]);

  function handleCopy() {
    navigator.clipboard.writeText(checkinUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-stone-900 font-display">QR Code</h2>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded-lg">
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center justify-center bg-stone-50 rounded-lg p-6 mb-4">
          {qrSvgUrl ? (
            <img src={qrSvgUrl} alt="QR Code" className="w-48 h-48" />
          ) : (
            <div className="w-48 h-48 animate-pulse bg-stone-200 rounded-lg" />
          )}
        </div>

        <p className="text-xs text-stone-500 text-center mb-4">
          Visitors scan this QR code to check in via Singpass
        </p>

        <div
          onClick={handleCopy}
          className="flex items-center gap-2 px-3 py-2 mb-4 bg-stone-50 border border-stone-200 rounded-lg cursor-pointer hover:bg-stone-100 transition-colors"
        >
          <span className="flex-1 text-xs text-stone-600 truncate select-all">{checkinUrl}</span>
          {copied ? (
            <Check size={14} className="text-green-600 shrink-0" />
          ) : (
            <Copy size={14} className="text-stone-400 shrink-0" />
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => onDownload(locationId, "svg")}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 bg-white border border-stone-300 rounded-lg hover:bg-stone-50"
          >
            <Download size={14} />
            SVG
          </button>
          <button
            onClick={() => onDownload(locationId, "png")}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-stone-700 bg-white border border-stone-300 rounded-lg hover:bg-stone-50"
          >
            <Download size={14} />
            PNG
          </button>
        </div>
      </div>
    </div>
  );
}
